import { useEffect, useState } from "react";
import { CalendarDays, MapPin, ArrowRight } from "lucide-react";
import { to12h, registerUrl, useOpenMeetings } from "@/data/openMeetings";

const fmtDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });

const manilaDate = (d) => new Date(d.getTime() + 8 * 3600e3).toISOString().slice(0, 10);

export function countdown(m, now = new Date()) {
  const start = new Date(`${m.date}T${m.start_time || "00:00"}:00+08:00`);
  const end = m.end_time ? new Date(`${m.date}T${m.end_time}:00+08:00`) : null;
  const mins = Math.round((start - now) / 60000);
  if (mins <= 0) return (end ? now < end : mins > -180) ? "Happening now" : null;
  if (mins < 60) return `Starts in ${mins} minute${mins === 1 ? "" : "s"}`;
  const days = Math.round((new Date(`${m.date}T00:00:00Z`) - new Date(`${manilaDate(now)}T00:00:00Z`)) / 864e5);
  if (days === 0) {
    const h = Math.floor(mins / 60);
    return `Starts today · in ${h} hour${h === 1 ? "" : "s"}`;
  }
  if (days === 1) return `Starts tomorrow at ${to12h(m.start_time)}`;
  return `Starts in ${days} days`;
}

const Countdown = ({ m, now }) => {
  const label = countdown(m, now);
  if (!label) return null;
  const soon = !label.includes("days");
  return (
    <span
      data-testid={`open-meeting-countdown-${m.code}`}
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-sans ${
        soon ? "bg-terracotta text-parchment-100" : "bg-parchment-100/15 text-parchment-100"
      }`}
    >
      {label}
    </span>
  );
};

const MeetingRow = ({ m, now }) => (
  <div data-testid={`open-meeting-${m.code}`} className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 py-5 border-b border-parchment-100/10 last:border-0">
    <div className="flex items-center gap-3 md:w-56 shrink-0">
      <span className="relative flex w-2.5 h-2.5">
        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
        <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-400" />
      </span>
      <span className="text-[10px] uppercase tracking-[0.28em] text-gold font-sans">Registration open</span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-mono text-[10px] text-parchment-100/50">{m.code}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="font-serif text-2xl text-parchment-100 leading-tight">{m.title}</div>
        <Countdown m={m} now={now} />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-parchment-100/75 font-sans">
        <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {fmtDate(m.date)} · {to12h(m.start_time)}{m.end_time ? ` – ${to12h(m.end_time)}` : ""}</span>
        {m.venue && <span className="flex items-center gap-1.5"><MapPin size={14} /> {m.venue}</span>}
      </div>
    </div>
    <a
      href={registerUrl(m)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`open-meeting-register-${m.code}`}
      className="group self-start md:self-center inline-flex items-center gap-2 rounded-full bg-gold text-cocoa-900 px-7 py-3 text-[11px] uppercase tracking-[0.22em] font-sans hover:bg-parchment-100 transition-colors"
    >
      Register <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
    </a>
  </div>
);

export default function OpenMeetingsRibbon() {
  const meetings = useOpenMeetings();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!meetings.length) return null;

  return (
    <section id="open-meetings" data-testid="open-meetings-ribbon" className="bg-sage-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
        {meetings.map((m) => <MeetingRow key={m.id} m={m} now={now} />)}
      </div>
    </section>
  );
}
